'use client'

import { uniqBy } from 'lodash'
import { useMemo, useState } from 'react'
import { useAudioInputs, useAudioOutputs, useMeetingManager, useVideoInputs } from 'amazon-chime-sdk-component-library-react'
import { DeviceType } from 'amazon-chime-sdk-component-library-react/lib/types'

import { Cog8ToothIcon } from '@heroicons/react/24/outline'

import { useTranslations } from '@/lib/next-shim'
import { DeviceSelectionDialog } from '@/shared/ui/dialog/DeviceSelectionDialog'

export type DeviceSelectionControlProps = {
  onSelectDeviceError?: (error: unknown) => void
  initialOpen?: boolean
}

const mapMediaDeviceToDeviceProps = (mediaDevices: DeviceType[], type: MediaDeviceKind, selectedDeviceId: string | null) => {
  return mediaDevices.map(({ deviceId, label }) => ({
    id: deviceId,
    label: label,
    isSelected: deviceId === selectedDeviceId,
    type,
  }))
}

export function DeviceSelectionControl(props: DeviceSelectionControlProps) {
  const { onSelectDeviceError, initialOpen = false } = props
  const t = useTranslations('meetingControls')
  const meetingManager = useMeetingManager()
  const { devices: audioInputDevices, selectedDevice: selectedAudioInputDevice } = useAudioInputs()
  const { devices: audioOutputDevices, selectedDevice: selectedAudioOuputDeviceId } = useAudioOutputs()
  const { devices: videoInputDevices, selectedDevice: selectedVideoInputDevice } = useVideoInputs()

  const selectedAudioInputDeviceId = typeof selectedAudioInputDevice === 'string' ? selectedAudioInputDevice : null
  const selectedVideoInputDeviceId = typeof selectedVideoInputDevice === 'string' ? selectedVideoInputDevice : null

  const [openDialog, setOpenDialog] = useState<boolean>(initialOpen)

  const devices = useMemo(() => {
    return uniqBy(
      [
        ...mapMediaDeviceToDeviceProps(audioInputDevices, 'audioinput', selectedAudioInputDeviceId),
        ...mapMediaDeviceToDeviceProps(audioOutputDevices, 'audiooutput', selectedAudioOuputDeviceId),
        ...mapMediaDeviceToDeviceProps(videoInputDevices, 'videoinput', selectedVideoInputDeviceId),
      ],
      'id'
    )
  }, [audioInputDevices, audioOutputDevices, videoInputDevices, selectedAudioInputDeviceId, selectedAudioOuputDeviceId, selectedVideoInputDeviceId])

  const handleSelectDevice = async (deviceId: string, type: MediaDeviceKind) => {
    if (selectedAudioInputDeviceId === deviceId || selectedAudioOuputDeviceId === deviceId || selectedVideoInputDeviceId === deviceId) return
    try {
      switch (type) {
        case 'audioinput':
          await meetingManager.startAudioInputDevice(deviceId)
          break
        case 'audiooutput':
          await meetingManager.startAudioOutputDevice(deviceId)
          break
        case 'videoinput':
          await meetingManager.startVideoInputDevice(deviceId)
          break
        default:
          break
      }
    } catch (error) {
      onSelectDeviceError?.(error)
    }
  }

  return (
    <>
      <div className="ds-tooltip" data-tip={t('settings')}>
        <button
          type="button"
          className="flex rounded-full bg-white/10 p-2 text-white shadow-sm hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          onClick={() => setOpenDialog(true)}
          aria-label={t('settings')}
        >
          <Cog8ToothIcon className="h-5 w-5" />
        </button>
      </div>
      <DeviceSelectionDialog devices={devices} onClose={() => setOpenDialog(false)} onSelectDevice={handleSelectDevice} open={openDialog} />
    </>
  )
}
